import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

const DOCK_ICON_FILES: Record<string, string> = {
    figma: 'figma.png',
    claude: 'claude.png',
    notes: 'Notes.png',
    photos: 'photos.png',
    x: 'x.png',
    mail: 'mail.png',
    trash: 'Trash.png',
    folder: 'folder.png',
    mov: 'mov.png',
    instagram: 'instagram-logo.png'
}

// Bare brand logos with no app tile of their own. They are set on a white
// macOS-style tile so they line up with the tiled PNGs above.
const TILE_ICON_FILES: Record<string, string> = {
    youtube: 'youtube.png'
}

/**
 * Renders a macOS-style app / file icon from the supplied PNG artwork.
 * Fills its square container via size-full + object-contain. Logos listed in
 * TILE_ICON_FILES get a tile matching the macOS icon grid (body inset ~9.8%,
 * corner radius ~22.5%), so they scale with the dock magnification.
 */
export const AppGlyph: FC<{ id: string; alt?: string }> = ({ id, alt }): ReactNode => {
    const tileFile = TILE_ICON_FILES[id]

    if (tileFile) {
        return (
            <span className='relative block size-full'>
                <span className='absolute inset-[9.8%] flex items-center justify-center rounded-[22.5%] bg-gradient-to-b from-white to-[#efefef] shadow-[0_1px_2px_rgba(0,0,0,0.2),inset_0_0_0_1px_rgba(0,0,0,0.06)]'>
                    <img
                        src={`/atelier/doc-icons/${tileFile}`}
                        alt={alt ?? ''}
                        draggable={false}
                        className='w-[60%] object-contain'
                    />
                </span>
            </span>
        )
    }

    const file = DOCK_ICON_FILES[id]
    if (!file) return null

    return (
        <img
            src={`/atelier/doc-icons/${file}`}
            alt={alt ?? ''}
            draggable={false}
            className='size-full object-contain'
        />
    )
}

export const MacBatteryGlyph: FC = (): ReactNode => (
    <svg width='24' height='12' viewBox='0 0 24 12' fill='none' aria-hidden='true'>
        <rect x='0.5' y='1.5' width='19' height='9' rx='2.4' stroke='currentColor' strokeOpacity='0.55' />
        <rect x='2' y='3' width='13' height='6' rx='1.2' fill='currentColor' />
        <path d='M21.5 4.2 v3.6 a1.6 1.6 0 0 0 1.2 -1.8 a1.6 1.6 0 0 0 -1.2 -1.8 Z' fill='currentColor' fillOpacity='0.55' />
    </svg>
)

export const SignalBarsGlyph: FC = (): ReactNode => (
    <svg width='17' height='11' viewBox='0 0 17 11' fill='currentColor' aria-hidden='true'>
        <rect x='0' y='8' width='2.6' height='3' rx='0.6' />
        <rect x='4.6' y='6' width='2.6' height='5' rx='0.6' />
        <rect x='9.2' y='3.5' width='2.6' height='7.5' rx='0.6' />
        <rect x='13.8' y='0.5' width='2.6' height='10.5' rx='0.6' />
    </svg>
)

export const WifiGlyph: FC = (): ReactNode => (
    <svg width='15' height='11' viewBox='0 0 15 11' fill='none' stroke='currentColor' strokeWidth='1.4' strokeLinecap='round' aria-hidden='true'>
        <path d='M1.4 3.6 A9 9 0 0 1 13.6 3.6' />
        <path d='M3.6 6 A5.6 5.6 0 0 1 11.4 6' />
        <path d='M5.9 8.4 A2.3 2.3 0 0 1 9.1 8.4' />
        <circle cx='7.5' cy='9.8' r='0.5' fill='currentColor' stroke='none' />
    </svg>
)

export const IOSBatteryGlyph: FC = (): ReactNode => (
    <svg width='28' height='13' viewBox='0 0 28 13' fill='none' aria-hidden='true'>
        <rect x='0.6' y='0.6' width='24' height='11.8' rx='3.4' stroke='currentColor' strokeOpacity='0.55' />
        <rect x='2.1' y='2.1' width='21' height='8.8' rx='2' fill='currentColor' />
        <path d='M26.4 4 v5 a2.1 2.1 0 0 0 0 -5 Z' fill='currentColor' fillOpacity='0.55' />
    </svg>
)

export const BellSlashGlyph: FC = (): ReactNode => (
    <svg width='14' height='14' viewBox='0 0 14 14' fill='none' aria-hidden='true'>
        <path d='M3 10 h8 l-1 -1.5 V6 a3 3 0 0 0 -6 0 v2.5 Z' fill='currentColor' />
        <path d='M5.6 11 a1.4 1.4 0 0 0 2.8 0' stroke='currentColor' strokeWidth='1.1' fill='none' strokeLinecap='round' />
        <line x1='1.6' y1='12.4' x2='12.4' y2='1.6' stroke='#000000' strokeWidth='2.4' strokeLinecap='round' />
        <line x1='1.6' y1='12.4' x2='12.4' y2='1.6' stroke='currentColor' strokeWidth='1.1' strokeLinecap='round' />
    </svg>
)

const VERIFIED_FILTER =
    'brightness(0) saturate(100%) invert(46%) sepia(74%) saturate(2419%) hue-rotate(180deg) brightness(101%) contrast(101%)'

export const VerifiedBadge: FC<{ className?: string }> = ({ className }): ReactNode => (
    <img
        src='/atelier/x_ui/check_mark.svg'
        alt='Verified'
        draggable={false}
        className={cn('object-contain', className)}
        style={{ filter: VERIFIED_FILTER }}
    />
)
